/* eslint-disable no-restricted-syntax */

import Immutable from 'immutable';
import { createSelector } from 'reselect';
import {
  selectCurrentProfileUserProfileName,
  selectCurrentProfileUserId,
  selectCurrentPortfolioVisibilityFilter,
} from '../Portfolio/selectors';
import { selectGalleryByPortfolioUser } from '../Galleries/selectors';
import { currentUserSelector } from '../Users/selectors';
import { selectPayload } from '../location/selectors';

const selectLatestPosts = (state) => state.Posts.get('latestPosts');

export const selectPostsById = (state) => state.Posts.get('byId');
export const selectPortfolioPostsByUser = (state) => state.Posts.get('byUser');

export const getPostById = (state, postId) =>
  selectPostsById(state).get(postId);

export const selectLatestPostsByType = createSelector(
  [selectLatestPosts, selectPostsById],
  (latestPostsById, posts) => {
    if (!latestPostsById) {
      return Immutable.Map();
    }

    // populate it with post data
    // latestPosts: viewedByUserId > blockId > postType > postId
    let result = Immutable.Map();

    latestPostsById.forEach((postsByBlock, userId) => {
      postsByBlock.forEach((postsByType, blockId) => {
        postsByType.forEach((postId, postType) => {
          result = result.setIn([userId, blockId, postType], posts.get(postId));
        });
      });
    });

    return result;
  }
);

// By default this returns the lastest 'share' post for a block by a user
// The other postType option is 'submit'
export const makeSelectLatestPostForBlockAsUser = (
  userId,
  blockId,
  postType = 'share'
) =>
  createSelector(
    selectLatestPostsByType,
    (latestPostsByType) => {
      const latestPosts = latestPostsByType.getIn(
        [userId, blockId],
        Immutable.Map()
      );
      return latestPosts.get(postType, null);
    }
  );

// Only used by EmbeddedActivity
export const makeSelectLatestPostsForBlockAsUser = (userId, blockId) =>
  createSelector(
    selectLatestPostsByType,
    (latestPostsByType) => {
      const latestPosts = latestPostsByType.getIn(
        [userId, blockId],
        Immutable.Map()
      );
      return latestPosts;
    }
  );

// Get the posts in the current profile user's portfolio
export const selectCurrentPortfolioPosts = createSelector(
  [
    selectCurrentProfileUserId,
    selectCurrentProfileUserProfileName,
    selectCurrentPortfolioVisibilityFilter,
    selectPostsById,
    selectGalleryByPortfolioUser,
  ],
  (userId, profileName, visibilityFilter, postsById, gallery) => {
    // Should this post appear in the current portfolio?
    let isPortfolioOn;
    if (visibilityFilter === 'show') {
      isPortfolioOn = true;
    } else if (visibilityFilter === 'hide') {
      isPortfolioOn = false;
    }

    const hasRightAuthor = (post) => {
      if (post.get('postedByGroup')) {
        return post.get('postedByGroupMembers').includes(userId);
      } else {
        return post.get('postedBy') === userId;
      }
    };

    const hasRightPortfolioSetting = (post) => {
      if (isPortfolioOn === undefined) {
        return true;
      } else {
        return post.get('isPortfolioOn') === isPortfolioOn;
      }
    };

    const orderedPostIds = gallery.getIn([profileName, 'posts']);

    const testPost = (post) =>
      hasRightAuthor(post) && hasRightPortfolioSetting(post);

    const filteredPosts = postsById.filter(testPost);
    let orderedPostsById = Immutable.OrderedMap({});

    if (orderedPostIds && filteredPosts) {
      for (const postId of orderedPostIds.values()) {
        if (filteredPosts.get(postId)) {
          orderedPostsById = orderedPostsById.set(
            postId,
            filteredPosts.get(postId)
          );
        }
      }
    }
    return orderedPostsById;
  }
);

export const selectPostsCounterByVisibilityFilter = createSelector(
  [
    selectCurrentProfileUserProfileName,
    selectCurrentPortfolioVisibilityFilter,
    selectGalleryByPortfolioUser,
  ],
  (profileName, visibilityFilter, portfolioPostsGallery) => {
    let allPosts = 0;
    if (visibilityFilter === 'show' || visibilityFilter === 'hide') {
      allPosts = portfolioPostsGallery.getIn([profileName, visibilityFilter]);
    } else if (visibilityFilter === 'all') {
      allPosts =
        portfolioPostsGallery.getIn([profileName, 'show']) +
        portfolioPostsGallery.getIn([profileName, 'hide']);
    }

    return allPosts;
  }
);

// Get morePostsAvailable in the current profile user's portfolio
export const selectCurrentPortfolioMorePostsAvailable = createSelector(
  [selectCurrentPortfolioPosts, selectPostsCounterByVisibilityFilter],
  (postsById, counterByVisibilityFilter) => {
    return postsById.size < counterByVisibilityFilter;
  }
);

export const getCurrentPost = (state, props) => {
  // ModalPost can get the post directly from the store by postId
  if (!props.post && props.postId) {
    return getPostById(state, props.postId);
  }

  return props.post;
};

export const selectCurrentPostCanBeEditedByUser = createSelector(
  [getCurrentPost, currentUserSelector, selectPayload],
  (post, currentUser, routePayload) => {
    if (!currentUser) {
      return false;
    }

    if (routePayload.get('page') === 'portfolio') {
      return routePayload.get('profileName') === currentUser.get('profileName');
    }

    return (
      post.get('postedBy') === currentUser.get('id') ||
      post.get('postedByGroupMembers').includes(currentUser.get('id'))
    );
  }
);

export const selectCurrentPostILikeOrNot = createSelector(
  [getCurrentPost, currentUserSelector],
  (post, currentUser) => {
    if (!currentUser) {
      return false;
    }

    return post.get('likedBy').includes(currentUser.get('profileName'));
  }
);
